import type React from 'react';
import { useLayoutEffect } from 'react';
import { applyDocumentMotion, DOCUMENT_MOTION_ATTRIBUTE, type ReducedMotionPreference } from './motion-preference';
import { MotionPreferenceContext } from './motion-preference-context';

export interface WordsHappenMotionProviderProps {
  /** The player's choice. `system` follows the device's `prefers-reduced-motion`. */
  preference: ReducedMotionPreference;
  /**
   * Keeps `data-wh-motion` on `<html>` in step, so the CSS fallbacks, portalled modals included, follow the choice.
   * Off in showcases, where several variants share one document.
   */
  syncDocument?: boolean;
  children: React.ReactNode;
}

/**
 * The app root's reduced motion choice: `useReducedMotion` below it returns the player's choice, falling back to the
 * device's setting for `system`, and `<html>` carries it for CSS. The attribute is set before the first paint.
 */
export const WordsHappenMotionProvider: React.FC<WordsHappenMotionProviderProps> = ({
  preference,
  syncDocument = true,
  children,
}) => {
  useLayoutEffect(() => {
    if (!syncDocument) {
      return;
    }
    applyDocumentMotion(preference);
    return () => document.documentElement.removeAttribute(DOCUMENT_MOTION_ATTRIBUTE);
  }, [preference, syncDocument]);

  return <MotionPreferenceContext.Provider value={preference}>{children}</MotionPreferenceContext.Provider>;
};
